import { type Cell, gridSize } from "../game/board";
import {
  type BoardSize,
  DEFAULT_BOARD_SIZE,
  GAME_MODE_NAMES,
  GameMode,
  MAX_BOARD_CELLS,
  MIN_BOARD_CELLS,
  TICK_MS,
} from "../game/constants";
import { Engine, type GameView, type SavedGame } from "../game/engine";
import * as storage from "../game/storage";
import type { ScoreCategory, Settings } from "../game/storage";
import { SoundPlayer } from "../audio/sound";
import { loadGameTextures } from "../render/assets";
import { BoardRenderer } from "../render/board-renderer";
import { confirmDialog, promptPlayerName, showAbout, showScores } from "./dialogs";
import { requireElement } from "./dom";

type Screen = "welcome" | "game";

interface Direction {
  readonly dx: number;
  readonly dy: number;
}

const KEY_DIRECTIONS = new Map<string, Direction>([
  ["ArrowUp", { dx: 0, dy: -1 }],
  ["w", { dx: 0, dy: -1 }],
  ["ArrowDown", { dx: 0, dy: 1 }],
  ["s", { dx: 0, dy: 1 }],
  ["ArrowLeft", { dx: -1, dy: 0 }],
  ["a", { dx: -1, dy: 0 }],
  ["ArrowRight", { dx: 1, dy: 0 }],
  ["d", { dx: 1, dy: 0 }],
]);

/** Longest frame gap turned into engine ticks. Stops a burst of ticks after a background tab. */
const MAX_FRAME_MS = 250;
/** Empty space kept around the board inside its frame, in CSS pixels. */
const BOARD_MARGIN_PX = 16;
/** Smallest size of one gem cell on screen, in CSS pixels. Keeps tiny frames readable. */
const MIN_CELL_PX = 16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseMode(value: string | undefined): GameMode | null {
  return Object.values(GameMode).find((mode) => mode === value) ?? null;
}

/** Keyboard focus starts near the middle of the board, at (3, 3) on the default 8 x 8. */
function centreCell(size: BoardSize): Cell {
  return { x: Math.floor((size.width - 1) / 2), y: Math.floor((size.height - 1) / 2) };
}

/** Matches the original's "%06d" score display. */
function formatScore(score: number): string {
  return String(score).padStart(6, "0");
}

function isTextField(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLSelectElement ||
    target instanceof HTMLTextAreaElement
  );
}

/**
 * Wires the page to the game. The engine owns the rules, the board renderer draws gems, and this
 * class handles the header, menu, dialogs, input (mouse, touch, and keyboard), and the frame loop.
 */
export class GweledApp {
  private readonly ui = {
    canvas: requireElement<HTMLCanvasElement>("board"),
    game: requireElement<HTMLElement>("game"),
    welcome: requireElement<HTMLElement>("welcome"),
    boardWidth: requireElement<HTMLInputElement>("board-width"),
    boardHeight: requireElement<HTMLInputElement>("board-height"),
    newGame: requireElement<HTMLButtonElement>("new-game"),
    pause: requireElement<HTMLButtonElement>("pause"),
    subtitle: requireElement<HTMLElement>("subtitle"),
    menuButton: requireElement<HTMLButtonElement>("menu-button"),
    menu: requireElement<HTMLElement>("main-menu"),
    menuSound: requireElement<HTMLButtonElement>("menu-sound"),
    menuVolume: requireElement<HTMLInputElement>("menu-volume"),
    menuVolumeValue: requireElement<HTMLOutputElement>("menu-volume-value"),
    menuHints: requireElement<HTMLButtonElement>("menu-hints"),
    menuScores: requireElement<HTMLButtonElement>("menu-scores"),
    menuAbout: requireElement<HTMLButtonElement>("menu-about"),
    progress: requireElement<HTMLElement>("progress"),
    progressFill: requireElement<HTMLElement>("progress-fill"),
    progressText: requireElement<HTMLElement>("progress-text"),
    score: requireElement<HTMLElement>("score"),
  };

  private readonly sound = new SoundPlayer();
  private settings: Settings = storage.loadSettings();
  private board: BoardRenderer | null = null;
  private readonly engine: Engine;
  private screen: Screen = "welcome";
  /** Size of the board in the current game. */
  private boardSize: BoardSize = DEFAULT_BOARD_SIZE;
  /** Keyboard cursor on the board. Only drawn once the keyboard has been used. */
  private focusCell: Cell = centreCell(DEFAULT_BOARD_SIZE);
  private pointerDown = false;
  private lastFrameTime: number | null = null;
  private tickBudgetMs = 0;
  private category: ScoreCategory = "normal";
  private returnFocusToMenuButton = false;

  /** Maps engine callbacks to the board renderer, the DOM, and the sound player. */
  private readonly view: GameView = {
    addGem: (gem) => this.renderer.addGem(gem),
    clearGems: () => this.renderer.clearGems(),
    moveGem: (gem, x, y, done) => this.renderer.moveGem(gem, x, y, done),
    fallGem: (gem, toY, delayMs, durationMs, done) =>
      this.renderer.fallGem(gem, toY, delayMs, durationMs, done),
    dropGem: (gem, toY, delayMs) => this.renderer.dropGem(gem, toY, delayMs),
    destroyGem: (gem, done) => this.renderer.destroyGem(gem, done),
    setGemEffect: (gem, effect) => this.renderer.setGemEffect(gem, effect),
    setGemsOpacity: (alpha) => this.renderer.setGemsOpacity(alpha),
    setGemsVisible: (visible) => this.renderer.setGemsVisible(visible),
    setEffectsVisible: (visible) => this.renderer.setEffectsVisible(visible),
    createCursor: (x, y) => this.renderer.createCursor(x, y),
    moveCursor: (cursorId, x, y) => this.renderer.moveCursor(cursorId, x, y),
    removeCursor: (cursorId) => this.renderer.removeCursor(cursorId),
    fadeCursor: (cursorId) => this.renderer.fadeCursor(cursorId),
    showScorePopup: (text, cx, cy) => this.renderer.showScorePopup(text, cx, cy),
    showMessage: (text, lifetimeMs) => this.renderer.showMessage(text, lifetimeMs),
    clearMessages: () => this.renderer.clearMessages(),
    setScore: (score) => {
      this.ui.score.textContent = formatScore(score);
    },
    setProgress: (fraction) => {
      this.ui.progressFill.style.width = `${fraction * 100}%`;
      // A zero-width fill would still paint its rounded end as a dot.
      this.ui.progressFill.hidden = fraction <= 0;
    },
    setProgressText: (text) => {
      this.ui.progressText.textContent = text;
    },
    setPauseLabel: (paused) => {
      this.ui.pause.textContent = paused ? "Resume" : "Pause";
    },
    setPauseVisible: (visible) => {
      this.ui.pause.hidden = !visible;
    },
    playSound: (effect) => this.sound.play(effect),
  };

  constructor() {
    this.engine = new Engine({
      view: this.view,
      onGameOver: (score, mode) => {
        void this.handleGameOver(score, mode);
      },
    });
  }

  /** Loads textures, binds the page, and offers to restore a game saved on the last visit. */
  async start(): Promise<void> {
    const textures = await loadGameTextures();
    this.board = new BoardRenderer(this.ui.canvas, textures);

    this.sound.enabled = this.settings.sound;
    this.sound.volume = this.settings.volume;
    this.engine.setHintsEnabled(this.settings.hints);
    this.syncMenuItems();
    this.restoreBoardSizeInputs();
    this.bindEvents();
    requestAnimationFrame(this.frame);

    if (storage.hasSavedGame()) {
      await this.offerSavedGame();
    }
  }

  private get renderer(): BoardRenderer {
    if (!this.board) throw new Error("The board renderer is not ready yet");
    return this.board;
  }

  // ---------------------------------------------------------------------------------------------
  // Frame loop
  // ---------------------------------------------------------------------------------------------

  private readonly frame = (now: number): void => {
    const last = this.lastFrameTime ?? now;
    this.lastFrameTime = now;
    this.tickBudgetMs += Math.min(now - last, MAX_FRAME_MS);
    while (this.tickBudgetMs >= TICK_MS) {
      this.tickBudgetMs -= TICK_MS;
      this.engine.tick();
    }

    if (this.screen === "game") {
      this.renderer.render(now);
    }
    requestAnimationFrame(this.frame);
  };

  /** Fits the board into the frame at the largest whole cell size that keeps it on screen. */
  private resizeBoard(): void {
    if (!this.board || this.screen !== "game") return;
    const frame = this.ui.game;
    const { width, height } = this.boardSize;
    const cellPx = Math.max(
      MIN_CELL_PX,
      Math.floor(
        Math.min(
          (frame.clientWidth - BOARD_MARGIN_PX) / width,
          (frame.clientHeight - BOARD_MARGIN_PX) / height,
        ),
      ),
    );
    const widthPx = cellPx * width;
    const heightPx = cellPx * height;
    this.ui.canvas.style.width = `${widthPx}px`;
    this.ui.canvas.style.height = `${heightPx}px`;
    this.renderer.resize(widthPx, heightPx);
  }

  // ---------------------------------------------------------------------------------------------
  // Screens and game lifecycle
  // ---------------------------------------------------------------------------------------------

  private showScreen(screen: Screen): void {
    this.screen = screen;
    const playing = screen === "game";
    this.ui.welcome.hidden = playing;
    this.ui.game.hidden = !playing;
    this.ui.newGame.hidden = !playing;

    if (playing) {
      this.resizeBoard();
    } else {
      this.ui.pause.hidden = true;
      this.ui.progress.hidden = true;
      this.ui.subtitle.textContent = "";
    }
  }

  private startGame(mode: GameMode, size: BoardSize, saved?: SavedGame): void {
    this.category = mode === GameMode.Timed ? "timed" : "normal";
    this.boardSize = size;
    this.focusCell = centreCell(size);
    // The renderer must match the board before the engine adds gems to it.
    this.renderer.setBoardSize(size);
    this.showScreen("game");

    const endless = mode === GameMode.Endless;
    this.ui.subtitle.textContent = GAME_MODE_NAMES[mode];
    this.ui.progress.hidden = endless;
    this.ui.pause.hidden = endless;
    this.view.setPauseLabel(false);

    if (saved) {
      this.engine.restore(saved);
    } else {
      this.engine.newGame(mode, size);
    }
    this.ui.canvas.focus({ preventScroll: true });
  }

  private goToWelcome(): void {
    this.engine.stop();
    this.hideFocus();
    this.showScreen("welcome");
    this.modeCards()[0]?.focus();
  }

  /** Asks before aborting a running game, like the original's abort dialog. */
  private async requestNewGame(): Promise<void> {
    if (this.engine.running) {
      const confirmed = await confirmDialog("Do you really want to abort this game?");
      if (!confirmed) return;
    }
    this.goToWelcome();
  }

  private togglePause(): void {
    this.engine.togglePause();
    this.ui.canvas.focus({ preventScroll: true });
  }

  private async offerSavedGame(): Promise<void> {
    const saved = storage.loadSavedGame();
    const restore = await confirmDialog("Do you want to restore the game you were playing?");
    storage.clearSavedGame();
    if (restore && saved) {
      this.startGame(saved.mode, gridSize(saved.board), saved);
    }
  }

  /** Saves a running game when the page is closed or reloaded. */
  private saveRunningGame(): void {
    if (this.screen === "game" && this.engine.running) {
      storage.saveGame(this.engine.snapshot());
    }
  }

  private async handleGameOver(score: number, mode: GameMode): Promise<void> {
    if (mode === GameMode.Endless) return;

    const category: ScoreCategory = mode === GameMode.Timed ? "timed" : "normal";
    let highlight: number | undefined;
    if (storage.qualifiesForHiscores(category, score)) {
      const name = await promptPlayerName(score);
      const index = storage.addHiscore(category, score, name);
      highlight = index >= 0 ? index : undefined;
    }
    await showScores(category, highlight);
  }

  // ---------------------------------------------------------------------------------------------
  // Mouse and touch
  // ---------------------------------------------------------------------------------------------

  private onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.sound.unlock();
    this.hideFocus();

    const cell = this.renderer.cellAt(event.clientX, event.clientY);
    if (!cell) return;
    this.ui.canvas.setPointerCapture(event.pointerId);
    this.pointerDown = true;
    this.engine.press(cell.x, cell.y);
  }

  /** Releasing on another cell than the press completes a drag (a swap). */
  private onPointerUp(event: PointerEvent): void {
    if (!this.pointerDown) return;
    this.pointerDown = false;

    const cell = this.renderer.cellAt(event.clientX, event.clientY);
    if (cell) this.engine.release(cell.x, cell.y);
  }

  // ---------------------------------------------------------------------------------------------
  // Keyboard
  // ---------------------------------------------------------------------------------------------

  private onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;

    if (key === "F10") {
      event.preventDefault();
      if (!this.isDialogOpen()) this.ui.menu.togglePopover();
      return;
    }

    // Dialogs and the menu handle their own keys. Text fields keep typing.
    if (
      event.defaultPrevented ||
      this.isDialogOpen() ||
      this.isMenuOpen() ||
      isTextField(event.target)
    ) {
      return;
    }
    this.sound.unlock();

    const direction = KEY_DIRECTIONS.get(key);
    if (this.screen === "welcome") {
      if (direction && direction.dy !== 0) {
        event.preventDefault();
        this.moveModeFocus(direction.dy);
      }
      return;
    }

    if (direction) {
      event.preventDefault();
      this.moveOrSwap(direction);
      return;
    }

    const onBoard = event.target === document.body || event.target === this.ui.canvas;
    switch (key) {
      case "Enter":
      case " ":
        if (onBoard) {
          event.preventDefault();
          this.selectFocusedCell();
        }
        break;
      case "Escape":
        this.engine.cancelSelection();
        break;
      case "p":
        if (this.engine.mode !== GameMode.Endless) {
          event.preventDefault();
          this.togglePause();
        }
        break;
      case "n":
        event.preventDefault();
        void this.requestNewGame();
        break;
      default:
        break;
    }
  }

  private moveFocus({ dx, dy }: Direction): void {
    this.focusCell = {
      x: clamp(this.focusCell.x + dx, 0, this.boardSize.width - 1),
      y: clamp(this.focusCell.y + dy, 0, this.boardSize.height - 1),
    };
    this.renderer.setFocusCell(this.focusCell);
  }

  /**
   * With a gem selected, an arrow key swaps it with the neighbour in that direction, so Enter then
   * an arrow is enough. Otherwise the arrow key moves the keyboard cursor.
   */
  private moveOrSwap(direction: Direction): void {
    const selected = this.engine.selectedCell;
    if (!selected) {
      this.moveFocus(direction);
      return;
    }

    const target = { x: selected.x + direction.dx, y: selected.y + direction.dy };
    const { width, height } = this.boardSize;
    if (target.x < 0 || target.x >= width || target.y < 0 || target.y >= height) {
      return;
    }
    this.focusCell = target;
    this.renderer.setFocusCell(target);
    this.engine.press(target.x, target.y);
  }

  /** Enter selects the focused gem, or swaps with it when a gem is already selected. */
  private selectFocusedCell(): void {
    this.renderer.setFocusCell(this.focusCell);
    this.engine.press(this.focusCell.x, this.focusCell.y);
  }

  private hideFocus(): void {
    this.board?.setFocusCell(null);
  }

  private isDialogOpen(): boolean {
    return document.querySelector("dialog[open]") !== null;
  }

  private isMenuOpen(): boolean {
    return this.ui.menu.matches(":popover-open");
  }

  /** Reads the W and H inputs on the start page. Out-of-range or blank values are corrected. */
  private readBoardSize(): BoardSize {
    return {
      width: this.readDimension(this.ui.boardWidth, DEFAULT_BOARD_SIZE.width),
      height: this.readDimension(this.ui.boardHeight, DEFAULT_BOARD_SIZE.height),
    };
  }

  private restoreBoardSizeInputs(): void {
    const size = storage.loadBoardSize();
    this.ui.boardWidth.value = String(size.width);
    this.ui.boardHeight.value = String(size.height);
  }

  /**
   * Clamps a size input to the allowed range, writes the corrected value back, and returns it.
   * A blank or non-numeric value becomes `fallback`.
   */
  private readDimension(input: HTMLInputElement, fallback: number): number {
    const parsed = Number.parseInt(input.value, 10);
    const value = clamp(Number.isNaN(parsed) ? fallback : parsed, MIN_BOARD_CELLS, MAX_BOARD_CELLS);
    input.value = String(value);
    return value;
  }

  private modeCards(): HTMLButtonElement[] {
    return Array.from(this.ui.welcome.querySelectorAll<HTMLButtonElement>(".mode-card"));
  }

  private moveModeFocus(delta: number): void {
    const cards = this.modeCards();
    const current = cards.indexOf(document.activeElement as HTMLButtonElement);
    const next = current < 0 ? 0 : clamp(current + delta, 0, cards.length - 1);
    cards[next]?.focus();
  }

  // ---------------------------------------------------------------------------------------------
  // Menu (F10 or the header button), settings, and dialogs
  // ---------------------------------------------------------------------------------------------

  /** Items that Up and Down move between. The volume slider is included so it can be reached. */
  private menuItems(): HTMLElement[] {
    return Array.from(this.ui.menu.querySelectorAll<HTMLElement>(".menu-item, .menu-slider"));
  }

  private closeMenu(): void {
    this.ui.menu.hidePopover();
  }

  private syncMenuItems(): void {
    this.ui.menuSound.setAttribute("aria-checked", String(this.settings.sound));
    this.ui.menuHints.setAttribute("aria-checked", String(this.settings.hints));
    this.ui.menuVolume.value = String(Math.round(this.settings.volume * 100));
    this.ui.menuVolumeValue.value = `${this.ui.menuVolume.value}%`;
  }

  private setSound(enabled: boolean): void {
    this.settings = { ...this.settings, sound: enabled };
    this.sound.enabled = enabled;
    storage.saveSettings(this.settings);
    this.syncMenuItems();
  }

  /** Takes the slider's 0 to 100 value. The menu stays open so the level can be tuned live. */
  private setVolume(percent: number): void {
    const volume = Math.min(1, Math.max(0, percent / 100));
    this.settings = { ...this.settings, volume };
    this.sound.volume = volume;
    storage.saveSettings(this.settings);
    this.ui.menuVolumeValue.value = `${Math.round(volume * 100)}%`;
  }

  private setHints(enabled: boolean): void {
    this.settings = { ...this.settings, hints: enabled };
    this.engine.setHintsEnabled(enabled);
    storage.saveSettings(this.settings);
    this.syncMenuItems();
  }

  private onMenuBeforeToggle(event: Event): void {
    const closing = (event as ToggleEvent).newState === "closed";
    this.returnFocusToMenuButton = closing && this.ui.menu.contains(document.activeElement);
  }

  private onMenuToggle(event: Event): void {
    const open = (event as ToggleEvent).newState === "open";
    this.ui.menuButton.setAttribute("aria-expanded", String(open));
    if (open) {
      this.syncMenuItems();
      this.menuItems()[0]?.focus();
    } else if (this.returnFocusToMenuButton) {
      this.ui.menuButton.focus();
    }
    this.returnFocusToMenuButton = false;
  }

  private onMenuKeyDown(event: KeyboardEvent): void {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();

    const items = this.menuItems();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    let next: number;
    if (current < 0) {
      next = step > 0 ? 0 : items.length - 1;
    } else {
      next = (current + step + items.length) % items.length;
    }
    items[next]?.focus();
  }

  private bindEvents(): void {
    const { canvas, newGame, pause, menu } = this.ui;

    for (const card of this.modeCards()) {
      card.addEventListener("click", () => {
        const mode = parseMode(card.dataset.mode);
        if (mode) this.startGame(mode, this.readBoardSize());
      });
    }
    const sizeInputs: [HTMLInputElement, number][] = [
      [this.ui.boardWidth, DEFAULT_BOARD_SIZE.width],
      [this.ui.boardHeight, DEFAULT_BOARD_SIZE.height],
    ];
    for (const [input, fallback] of sizeInputs) {
      input.min = String(MIN_BOARD_CELLS);
      input.max = String(MAX_BOARD_CELLS);
      input.addEventListener("change", () => {
        this.readDimension(input, fallback);
        storage.saveBoardSize(this.readBoardSize());
      });
    }

    newGame.addEventListener("click", () => {
      void this.requestNewGame();
    });
    pause.addEventListener("click", () => {
      this.togglePause();
    });

    canvas.addEventListener("pointerdown", (event) => {
      this.onPointerDown(event);
    });
    canvas.addEventListener("pointerup", (event) => {
      this.onPointerUp(event);
    });
    canvas.addEventListener("pointercancel", () => {
      this.pointerDown = false;
    });
    window.addEventListener("keydown", (event) => {
      this.onKeyDown(event);
    });

    menu.addEventListener("beforetoggle", (event) => {
      this.onMenuBeforeToggle(event);
    });
    menu.addEventListener("toggle", (event) => {
      this.onMenuToggle(event);
    });
    menu.addEventListener("keydown", (event) => {
      this.onMenuKeyDown(event);
    });

    this.ui.menuSound.addEventListener("click", () => {
      this.closeMenu();
      this.setSound(!this.settings.sound);
    });
    this.ui.menuVolume.addEventListener("input", () => {
      this.setVolume(Number(this.ui.menuVolume.value));
    });
    this.ui.menuHints.addEventListener("click", () => {
      this.closeMenu();
      this.setHints(!this.settings.hints);
    });
    this.ui.menuScores.addEventListener("click", () => {
      this.closeMenu();
      void showScores(this.category);
    });
    this.ui.menuAbout.addEventListener("click", () => {
      this.closeMenu();
      void showAbout();
    });

    new ResizeObserver(() => {
      this.resizeBoard();
    }).observe(this.ui.game);

    // Timed games pause when the window loses focus, like the original.
    window.addEventListener("blur", () => {
      this.engine.pauseIfTimed();
    });
    window.addEventListener("pagehide", () => {
      this.saveRunningGame();
    });
  }
}
